import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as constants from '../../constants/CONSTANT';
import DataApi from '../../api/DataApi';
import MultiSelectDropdown from '../layout/MultiSelectDropdown1';
import jwt_decode from 'jwt-decode';
import {
  fetchRecordViewConfigRows,
  fetchObjectMetaBatch,
  configRowsToMapByObject,
} from './recordViewLayoutRoleApi';
import './css/PermissionBulkEdit.css';
import './css/RecordViewLayoutTable.css';

const PermissionRecordViewLayoutAssign = () => {
  const { id: roleId } = useParams();
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [userRole, setUserRole] = useState('');
  const [recordviewConfigByObject, setRecordviewConfigByObject] = useState({});
  const [originalRecordviewConfigByObject, setOriginalRecordviewConfigByObject] = useState({});
  const [objectMetaByObject, setObjectMetaByObject] = useState({});

  const roleApi = new DataApi('roles');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwt_decode(token);
        setUserRole(decoded?.role_name || '');
      } catch (e) {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    if (roleId) {
      loadAll();
    }
  }, [roleId]);

  const patchRecordviewConfig = (objectName, patch) => {
    setRecordviewConfigByObject((prev) => ({
      ...prev,
      [objectName]: {
        recordviewIds: [],
        defaultDetailLayoutId: '',
        defaultEditLayoutId: '',
        ...(prev[objectName] || {}),
        ...patch,
      },
    }));
  };

  const recordviewConfigChanged = () => {
    const keys = new Set([
      ...Object.keys(recordviewConfigByObject),
      ...Object.keys(originalRecordviewConfigByObject),
    ]);
    const norm = (cfg) => ({
      rv: [...(cfg?.recordviewIds || [])].map(String).sort().join('|'),
      d: cfg?.defaultDetailLayoutId || '',
      e: cfg?.defaultEditLayoutId || '',
    });
    for (const k of keys) {
      const a = norm(recordviewConfigByObject[k]);
      const b = norm(originalRecordviewConfigByObject[k]);
      if (a.rv !== b.rv || a.d !== b.d || a.e !== b.e) return true;
    }
    return false;
  };

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: token };
      const [roleRes, permRes] = await Promise.all([
        roleApi.fetchById(roleId),
        axios.get(`${constants.API_BASE_URL}/api/permissions/role/${roleId}`, { headers }),
      ]);
      if (roleRes.data?.success) setRole(roleRes.data.data);
      if (!permRes.data?.success) {
        setError(permRes.data?.message || 'Failed to load permissions');
        return;
      }
      const permissionsData = permRes.data.data || [];
      setPermissions(permissionsData);

      const objectNames = [...new Set(permissionsData.map((p) => p.object_name).filter(Boolean))];
      const [configRows, metaByObject] = await Promise.all([
        fetchRecordViewConfigRows(roleId),
        fetchObjectMetaBatch(objectNames),
      ]);
      const cfgMap = configRowsToMapByObject(configRows);
      const byObj = {};
      for (const on of objectNames) {
        const row = cfgMap[on];
        byObj[on] = row
          ? {
              recordviewIds: (row.recordview_ids || []).map(String),
              defaultDetailLayoutId: row.default_detail_layout_id
                ? String(row.default_detail_layout_id)
                : '',
              defaultEditLayoutId: (() => {
                const e = row.default_edit_layout_id ? String(row.default_edit_layout_id) : '';
                const d = row.default_detail_layout_id ? String(row.default_detail_layout_id) : '';
                return e || d;
              })(),
            }
          : { recordviewIds: [], defaultDetailLayoutId: '', defaultEditLayoutId: '' };
      }
      setRecordviewConfigByObject(byObj);
      setOriginalRecordviewConfigByObject(JSON.parse(JSON.stringify(byObj)));
      setObjectMetaByObject(metaByObject);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!recordviewConfigChanged()) {
      setError('No changes to save.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const items = permissions.map((p) => {
        const c = recordviewConfigByObject[p.object_name] || {};
        return {
          object_name: p.object_name,
          recordview_ids: c.recordviewIds || [],
          default_detail_layout_id: c.defaultDetailLayoutId || null,
          default_edit_layout_id: c.defaultEditLayoutId || null,
        };
      });
      const res = await axios.put(
        `${constants.API_BASE_URL}/api/permissions/role/${roleId}/recordview-config`,
        { items },
        { headers: { Authorization: token } }
      );
      if (res.data?.success) {
        setOriginalRecordviewConfigByObject(JSON.parse(JSON.stringify(recordviewConfigByObject)));
        navigate(
          `/setup/permission/${roleId}?success=true&message=${encodeURIComponent('Record views and layouts saved.')}`
        );
      } else {
        setError(res.data?.message || 'Save failed');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const roleName = role?.fields?.find((f) => f.field_name === 'name')?.value || role?.name || '';
  const isAdminEditingOwnRole =
    String(roleName || '').toUpperCase() === 'ADMIN' && String(userRole || '').toUpperCase() === 'ADMIN';

  const filteredPermissions = useMemo(
    () =>
      permissions.filter(
        (permission) =>
          permission.object_display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          permission.object_name?.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [permissions, searchTerm]
  );

  const standardPermissions = filteredPermissions.filter((p) => p.is_standard === true);
  const customPermissions = filteredPermissions.filter((p) => p.is_standard !== true);

  const renderEditableRow = (permission) => {
    const on = permission.object_name;
    const meta = objectMetaByObject[on] || { recordviews: [], layouts: [], error: null };
    const cfg = recordviewConfigByObject[on] || {
      recordviewIds: [],
      defaultDetailLayoutId: '',
      defaultEditLayoutId: '',
    };
    const assignableRvs = (meta.recordviews || []).filter((r) => !r.is_deleted && r.is_active !== false);
    const picklistValues = assignableRvs.map((r) => ({
      key: String(r.id),
      value: `${r.view_label || r.name || r.id}`,
    }));
    const layouts = meta.layouts || [];
    const detailLayouts = layouts.filter((l) => String(l.layout_type || '').toUpperCase() === 'DETAIL');

    const origRvIds = (originalRecordviewConfigByObject[on]?.recordviewIds || []).map(String);
    const lockLayoutClear =
      isAdminEditingOwnRole &&
      !!(originalRecordviewConfigByObject[on]?.defaultDetailLayoutId ||
        originalRecordviewConfigByObject[on]?.defaultEditLayoutId);

    return (
      <tr key={permission.id || on}>
        <td className="rv-layout-col-object fw-medium">
          {permission.object_display_name || on}
        </td>
        <td className="rv-layout-col-rv">
          {meta.error && <div className="text-warning small">{meta.error}</div>}
          {assignableRvs.length === 0 && !meta.error ? (
            <span className="text-muted small">No record views</span>
          ) : (
            <MultiSelectDropdown
              picklistValues={picklistValues}
              selectedValues={(cfg.recordviewIds || []).map(String)}
              onChange={(keys) => patchRecordviewConfig(on, { recordviewIds: keys })}
              placeholder="Record views…"
              disabled={!!meta.error}
              lockedKeys={isAdminEditingOwnRole ? origRvIds : []}
            />
          )}
        </td>
        <td className="rv-layout-col-layout">
          <select
            className="form-select form-select-sm"
            value={cfg.defaultDetailLayoutId || cfg.defaultEditLayoutId || ''}
            onChange={(e) => {
              const v = e.target.value || '';
              if (lockLayoutClear && !v) return;
              patchRecordviewConfig(on, { defaultDetailLayoutId: v, defaultEditLayoutId: v });
            }}
            disabled={!!meta.error}
          >
            <option value="" disabled={lockLayoutClear}>
              — None —
            </option>
            {detailLayouts.map((l) => (
              <option key={l.layout_id} value={l.layout_id}>
                {l.layout_name || l.layout_id}
              </option>
            ))}
          </select>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="permission-bulk-edit-page permission-recordview-assign-page">
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="permission-bulk-edit-page permission-recordview-assign-page">
      <div className="bulk-edit-header">
        <div className="header-left">
          <button type="button" className="btn-back" onClick={() => navigate(`/setup/permission/${roleId}`)}>
            <i className="fas fa-arrow-left" />
            Back
          </button>
          <div className="header-info">
            <h1>Assign record view / layout</h1>
            <p>
              Role: {role?.fields?.find((f) => f.field_name === 'name')?.value || role?.name || '—'}
            </p>
          </div>
        </div>
        {permissions.length > 0 && (
          <div className="header-actions">
            <button type="button" className="btn-save" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <i className="fas fa-save" />
                  Save
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="error-alert mx-3">
          <i className="fas fa-exclamation-triangle" />
          {error}
        </div>
      )}

      {isAdminEditingOwnRole && (
        <div className="alert alert-info mx-3 mt-2">
          For the <strong>Admin</strong> role you may <strong>add</strong> record views and pick or change default
          layouts, but you cannot <strong>remove</strong> a record view or <strong>clear</strong> a default layout that
          was already saved. The server applies the same rules so reductions are ignored.
        </div>
      )}

      {permissions.length === 0 ? (
        <div className="px-3 py-4 text-muted">No object permissions for this role. Add permissions first.</div>
      ) : (
        <>
          <div className="assign-table-toolbar px-3">
            <div className="search-box d-flex align-items-center gap-2" style={{ maxWidth: 360 }}>
              <i className="fas fa-search text-muted" />
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Search objects…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="assign-table-wrap">
            <div className="table-responsive">
              <table className="table table-sm table-bordered align-middle recordview-layout-table">
                <thead className="table-light">
                  <tr>
                    <th>Object name</th>
                    <th>Record view</th>
                    <th>Detail layout (view &amp; edit)</th>
                  </tr>
                </thead>
                <tbody>
                  {standardPermissions.length > 0 && (
                    <>
                      <tr className="recordview-layout-section-row">
                        <td colSpan="3">Standard objects</td>
                      </tr>
                      {standardPermissions.map(renderEditableRow)}
                    </>
                  )}
                  {customPermissions.length > 0 && (
                    <>
                      <tr className="recordview-layout-section-row">
                        <td colSpan="3">Custom objects</td>
                      </tr>
                      {customPermissions.map(renderEditableRow)}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PermissionRecordViewLayoutAssign;
